import apiClient from '@/lib/apiClient';
import { ApiResponse } from '@/types';

export interface EmailPreviewBlock {
  id: string;
  type: string;
  html: string;
}

export interface EmailPreviewResult {
  subject: string;
  bodyHtml: string;
  bodyText: string;
  blocks: EmailPreviewBlock[];
  layoutBefore: string;
  layoutAfter: string;
  referenceNumber: string | null;
  defaultTo: string | null;
  defaultCc: string | null;
  defaultBcc: string | null;
  replyTo: string | null;
}

export interface SendEmailPayload {
  templateSlug: string;
  leadId: number;
  to: string[];
  cc?: string[];
  bcc?: string[];
  replyTo?: string;
  subject: string;
  bodyHtml: string;
  bodyText: string;
  // RM-chosen From address (a configured sender, or the RM's own email).
  fromEmail?: string;
}

export interface SendEmailResult {
  emailLogId: number;
  referenceNumber: string | null;
  status: string;
}

// Fetches the server-rendered PDF (identical to the emailed attachment) and
// triggers a browser download (Fix 1). The server builds it via pdfmake, so the
// formatting matches the email exactly - the old client jsPDF path is gone.
const downloadLeadPdf = async (
  url: string,
  leadId: number,
  fallbackName: string,
): Promise<void> => {
  const response = await apiClient.get<Blob>(url, {
    params: { leadId },
    responseType: 'blob',
  });
  const disposition = response.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? fallbackName;

  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const emailService = {
  // `bankLabel` chooses which company bank account's details the rendered
  // template shows (ADR-020); blank/undefined uses the first saved account.
  preview: async (
    templateSlug: string,
    leadId: number,
    bankLabel?: string,
  ): Promise<EmailPreviewResult> => {
    const { data } = await apiClient.get<ApiResponse<EmailPreviewResult>>('/email/preview', {
      params: { templateSlug, leadId, ...(bankLabel ? { bankLabel } : {}) },
    });
    if (!data.data) throw new Error('Email preview not found in response');
    return data.data;
  },

  send: async (payload: SendEmailPayload): Promise<SendEmailResult> => {
    // A send renders + attaches PDFs and round-trips the mail provider, so it is
    // far heavier than the 10s default. On a slow connection the default aborted
    // the request client-side while the server completed the send, leaving the
    // UI stuck (button grayed, no log). Give it a generous per-request timeout.
    const { data } = await apiClient.post<ApiResponse<SendEmailResult>>('/email/send', payload, {
      timeout: 60_000,
    });
    if (!data.data) throw new Error('Email send result not found in response');
    return data.data;
  },

  downloadQuotationPdf: (leadId: number): Promise<void> =>
    downloadLeadPdf('/email/quotation-pdf', leadId, `quotation-${leadId}.pdf`),
  downloadClientInfoPdf: (leadId: number): Promise<void> =>
    downloadLeadPdf('/email/client-info-pdf', leadId, `client-info-${leadId}.pdf`),
};
